import React, { useRef, useLayoutEffect, useState } from 'react';
import { cn } from '../utils/cn';
import Button from './Button';
import { motion } from 'framer-motion';

const HeroOverlayMobile = ({ 
  onProfessionalClick, 
  onHouseholdClick,
  onProfessionalHover,
  onHouseholdHover,
  onButtonLeave,
}) => {
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    function handleResize() {
      if (!contentRef.current) return;
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const maxHeight = vh - 0.5 * 16;
      const maxWidth = vw - 2 * 16;
      const contentHeight = contentRef.current.offsetHeight;
      const contentWidth = contentRef.current.offsetWidth;
      const scaleH = maxHeight / contentHeight;
      const scaleW = maxWidth / contentWidth;
      const newScale = Math.min(scaleH, scaleW, 1);
      setScale(newScale);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none py-0.5">
      <div
        ref={contentRef}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'center',
          transition: 'transform 0.2s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '-0.25rem',
          width: '100%'
        }}
      >
        {/* RR Mascot */}
        <motion.div 
          className="flex items-center justify-center w-[288px] h-[288px]"
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <img 
            src="/images/RRMascot+Type-smaller.png" 
            alt="Rugged Red Mascot" 
            className="w-full h-full object-contain"
          />
        </motion.div>

        {/* Headline */}
        <motion.div 
          className="rounded-xl w-full flex justify-center items-center mx-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          style={{ 
            filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5)) drop-shadow(0 8px 16px rgba(0, 0, 0, 0.3))"
          }}
        >
          <motion.div className="backdrop-filter backdrop-blur-md rounded-xl drop-shadow-2xl w-full h-full p-2">
            <motion.h1 
              className="font-extrabold leading-[0.88] tracking-tight text-white text-center whitespace-normal text-[1.65rem] geogrotesque-heading"
              style={{ 
                textShadow: "2px 2px 4px rgba(0, 0, 0, 0.6), 4px 4px 8px rgba(0, 0, 0, 0.4)",
                letterSpacing: "-0.02em"
              }}
            >
              Choose the Professional-Grade Cleaning Solution Best for You.
            </motion.h1>
          </motion.div>
        </motion.div>

        {/* Buttons */}
        <motion.div 
          className="flex flex-row justify-between w-full mt-4 pointer-events-auto px-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)'
          }}
        >
          <div className="w-[48%]">
            <Button
              variant="primary"
              size="extra-large"
              onClick={onProfessionalClick}
              onMouseEnter={onProfessionalHover}
              onMouseLeave={onButtonLeave}
              className="rounded-[2rem] transition-colors duration-300 min-w-[80px] sm:min-w-[100px] text-xl sm:text-2xl py-2 sm:py-3 px-3 sm:px-4 w-full"
              style={{ 
                filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.7)) drop-shadow(4px 4px 8px rgba(0, 0, 0, 0.5)) drop-shadow(6px 6px 12px rgba(0, 0, 0, 0.3))"
              }}
            >
              Industrial
            </Button>
          </div>
          <div className="w-[48%]">
            <Button
              variant="primary"
              size="extra-large"
              onClick={onHouseholdClick}
              onMouseEnter={onHouseholdHover}
              onMouseLeave={onButtonLeave}
              className="rounded-[2rem] transition-colors duration-300 min-w-[80px] sm:min-w-[100px] text-xl sm:text-2xl py-2 sm:py-3 px-3 sm:px-4 w-full"
              style={{ 
                filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.7)) drop-shadow(4px 4px 8px rgba(0, 0, 0, 0.5)) drop-shadow(6px 6px 12px rgba(0, 0, 0, 0.3))"
              }}
            >
              Household
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const HeroOverlayDesktop = ({ 
  onProfessionalClick, 
  onHouseholdClick,
  onProfessionalHover,
  onHouseholdHover,
  onButtonLeave,
}) => {
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    function handleResize() {
      if (!contentRef.current) return;
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const maxHeight = vh - 6 * 16;
      const maxWidth = vw - 1.5 * 16;
      const contentHeight = contentRef.current.offsetHeight;
      const contentWidth = contentRef.current.offsetWidth;
      const scaleH = maxHeight / contentHeight;
      const scaleW = maxWidth / contentWidth;
      const newScale = Math.min(scaleH, scaleW, 2.5);
      setScale(newScale);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none py-12 w-full">
      <div
        ref={contentRef}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'center',
          transition: 'transform 0.2s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '-1.2rem',
          width: '4000px'
        }}
      >
        {/* RR Mascot */}
        <motion.div 
          className="flex items-center justify-center w-[936px] h-[936px] sm:w-[1116px] sm:h-[1116px] md:w-[1296px] md:h-[1296px] lg:w-[1476px] lg:h-[1476px]"
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <img 
            src="/images/RRMascot+Type-smaller.png" 
            alt="Rugged Red Mascot" 
            className="w-full h-full object-contain"
          />
        </motion.div>

        {/* Headline */}
        <motion.div 
          className="rounded-3xl w-full flex justify-center items-center mx-4 sm:mx-8 md:mx-12 lg:mx-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          style={{ 
            width: '4000px',
            filter: "drop-shadow(0 8px 16px rgba(0, 0, 0, 0.5)) drop-shadow(0 16px 32px rgba(0, 0, 0, 0.3))"
          }}
        >
          <motion.div className="backdrop-filter backdrop-blur-md rounded-3xl drop-shadow-2xl w-full h-full p-8 sm:p-12 md:p-16 lg:p-20">
            <motion.h1 
              className="font-extrabold leading-[0.88] tracking-tight text-white text-center whitespace-normal text-[9.9rem] sm:text-[11.55rem] md:text-[13.2rem] lg:text-[14.85rem] geogrotesque-heading"
              style={{ 
                textShadow: "4px 4px 8px rgba(0, 0, 0, 0.6), 8px 8px 16px rgba(0, 0, 0, 0.4)",
                letterSpacing: "-0.02em"
              }}
            >
              Choose the Professional-Grade Cleaning Solution Best for You.
            </motion.h1>
          </motion.div>
        </motion.div>

        {/* Buttons */}
        <motion.div 
          className="flex flex-row justify-between mx-4 sm:mx-8 md:mx-12 lg:mx-16 mt-12 pointer-events-auto w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 56px)',
            width: '4000px'
          }}
        >
          <div className="w-1/3 flex justify-end pr-8">
            <Button
              variant="primary"
              size="extra-large"
              onClick={onProfessionalClick}
              onMouseEnter={onProfessionalHover}
              onMouseLeave={onButtonLeave}
              className="rounded-[2rem] transition-colors duration-300 min-w-[300px] sm:min-w-[400px] md:min-w-[500px] lg:min-w-[600px] text-8xl sm:text-9xl md:text-[10rem] lg:text-[11rem] py-16 sm:py-20 md:py-24 lg:py-28 px-12 sm:px-16 md:px-20 lg:px-24"
              style={{ 
                filter: "drop-shadow(4px 4px 8px rgba(0, 0, 0, 0.7)) drop-shadow(8px 8px 16px rgba(0, 0, 0, 0.5)) drop-shadow(12px 12px 24px rgba(0, 0, 0, 0.3))"
              }}
            >
              Industrial
            </Button>
          </div>
          <div className="w-1/3 flex justify-center px-8">
            {/* Center space */}
          </div>
          <div className="w-1/3 flex justify-start pl-8">
            <Button
              variant="primary"
              size="extra-large"
              onClick={onHouseholdClick}
              onMouseEnter={onHouseholdHover}
              onMouseLeave={onButtonLeave}
              className="rounded-[2rem] transition-colors duration-300 min-w-[300px] sm:min-w-[400px] md:min-w-[500px] lg:min-w-[600px] text-8xl sm:text-9xl md:text-[10rem] lg:text-[11rem] py-16 sm:py-20 md:py-24 lg:py-28 px-12 sm:px-16 md:px-20 lg:px-24"
              style={{ 
                filter: "drop-shadow(4px 4px 8px rgba(0, 0, 0, 0.7)) drop-shadow(8px 8px 16px rgba(0, 0, 0, 0.5)) drop-shadow(12px 12px 24px rgba(0, 0, 0, 0.3))"
              }}
            >
              Household
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const HeroOverlay = ({ 
  className, 
  onProfessionalClick, 
  onHouseholdClick,
  onProfessionalHover,
  onHouseholdHover,
  onButtonLeave,
  isMobile
}) => {
  return isMobile ? (
    <HeroOverlayMobile
      onProfessionalClick={onProfessionalClick}
      onHouseholdClick={onHouseholdClick}
      onProfessionalHover={onProfessionalHover}
      onHouseholdHover={onHouseholdHover}
      onButtonLeave={onButtonLeave}
    />
  ) : (
    <HeroOverlayDesktop
      onProfessionalClick={onProfessionalClick}
      onHouseholdClick={onHouseholdClick}
      onProfessionalHover={onProfessionalHover}
      onHouseholdHover={onHouseholdHover}
      onButtonLeave={onButtonLeave}
    />
  );
};

export default HeroOverlay; 